import type { PixelBuffer } from "./PixelBuffer";
import type { BrushSettings } from "./brushSettings";
import { applyJitter, mixFgBg, applyHueJitter, applyPurity } from "./brushSettings";
import type { BlendMode } from "../document/Layer";
import {
  blendMultiply, blendScreen, blendDarken, blendLighten,
  blendColorBurn, blendColorDodge, blendLinearBurn, blendLinearDodge,
  blendOverlay, blendHardLight, blendSoftLight, blendVividLight,
  blendLinearLight, blendPinLight, blendHardMix,
  blendDifference, blendExclusion, blendSubtract, blendDivide,
} from "./blend";

// Stamp a soft-edged round brush onto a pixel buffer. Operates in document
// coordinates. Returns the bbox of pixels touched (or null if fully clipped).
//
// `mode` selects compositing:
//   "paint" — source-over with the given RGB color
//   "erase" — destination-out (reduces dst alpha by mask alpha)
//
// `selMask` — optional selection mask. If provided, pixels where mask === 0
// are skipped. Partial mask values (1..254) modulate the brush alpha.
// This is how Photoshop enforces selection boundaries on all brush tools.

export type BrushMode = "paint" | "erase";

export interface BrushStamp {
  cx: number;
  cy: number;
  radius: number;   // outer radius in doc px
  hardness: number; // 0..1; 1 = hard edge, 0 = fully feathered
  opacity: number;  // 0..1 stamp opacity
  r: number;        // 0..255
  g: number;
  b: number;
  mode: BrushMode;
  /** Optional selection mask. If set, only paints where mask > 0. */
  selMask?: Uint8Array | undefined;
  /** Width of the selection mask (same as document width). */
  selMaskW?: number | undefined;
  /** Optional lock-transparency mask (255 = opaque, 0 = transparent).
   *  When present, painting is restricted to pixels where lockAlpha > 0. */
  lockAlpha?: Uint8Array | undefined;
  /** Width of the lockAlpha mask (same as layer width). */
  lockAlphaW?: number | undefined;
  /** Optional roundness (0..1, 1 = circle). When < 1, brush is elliptical. */
  roundness?: number | undefined;
  /** Optional angle in radians for elliptical brush rotation. */
  angle?: number | undefined;
  /** Wet edges mode: boosts paint at brush perimeter. */
  wetEdges?: boolean | undefined;
  /**
   * Blend mode for the stroke (Photoshop brush options bar).
   * "normal" (default) uses plain source-over.
   * "behind" paints only into transparent areas (dst-over respecting alpha).
   * "clear" erases: output alpha = dstA * (1 - srcA).
   * All other values must match BlendMode keys from blend.ts.
   */
  blendMode?: string | undefined;
  /**
   * When erasing (`mode === "erase"`) on a locked Background layer, there's
   * no transparency to reveal — Photoshop's Eraser instead paints this
   * color (the current background color) at the stamp's alpha, same as a
   * plain "paint" stamp, rather than reducing dst alpha. Omitted (default)
   * preserves the original alpha-reduction erase behavior exactly (#i165).
   * Values are 0..255, matching `r`/`g`/`b` above.
   */
  eraseToColor?: { r: number; g: number; b: number } | undefined;
}

export interface BBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

// Map from BlendMode string → per-channel (d, s) → blended channel 0..1.
// Only separable modes. Non-separable (hue/saturation/color/luminosity) and
// whole-pixel modes (darker-color, lighter-color, dissolve) are handled
// separately in applyBlendMode below.
type SepFn = (d: number, s: number) => number;

const SEP_MODE_MAP: Partial<Record<string, SepFn>> = {
  multiply: blendMultiply,
  screen: blendScreen,
  darken: blendDarken,
  lighten: blendLighten,
  "color-burn": blendColorBurn,
  "color-dodge": blendColorDodge,
  "linear-burn": blendLinearBurn,
  "linear-dodge": blendLinearDodge,
  overlay: blendOverlay,
  "hard-light": blendHardLight,
  "soft-light": blendSoftLight,
  "vivid-light": blendVividLight,
  "linear-light": blendLinearLight,
  "pin-light": blendPinLight,
  "hard-mix": blendHardMix,
  difference: blendDifference,
  exclusion: blendExclusion,
  subtract: blendSubtract,
  divide: blendDivide,
};

// Non-separable lum helpers (inline, no DOM).
const LUM_R = 0.3, LUM_G = 0.59, LUM_B = 0.11;
type RGB3 = [number, number, number];

function lumRGB(r: number, g: number, b: number): number {
  return LUM_R * r + LUM_G * g + LUM_B * b;
}

function clipColor(r: number, g: number, b: number): RGB3 {
  const l = lumRGB(r, g, b);
  const n = Math.min(r, g, b);
  const x = Math.max(r, g, b);
  if (n < 0) {
    r = l + ((r - l) * l) / (l - n);
    g = l + ((g - l) * l) / (l - n);
    b = l + ((b - l) * l) / (l - n);
  }
  if (x > 1) {
    r = l + ((r - l) * (1 - l)) / (x - l);
    g = l + ((g - l) * (1 - l)) / (x - l);
    b = l + ((b - l) * (1 - l)) / (x - l);
  }
  return [r, g, b];
}

function setLumRGB(r: number, g: number, b: number, l: number): RGB3 {
  const d = l - lumRGB(r, g, b);
  return clipColor(r + d, g + d, b + d);
}

function satRGB(r: number, g: number, b: number): number {
  return Math.max(r, g, b) - Math.min(r, g, b);
}

function setSatRGB(r: number, g: number, b: number, s: number): RGB3 {
  const arr: [number, 0 | 1 | 2][] = [[r, 0], [g, 1], [b, 2]];
  arr.sort((a, b) => a[0] - b[0]);
  const out: [number, number, number] = [0, 0, 0];
  const [lo, mid, hi] = arr as [[number, 0|1|2], [number, 0|1|2], [number, 0|1|2]];
  if (hi[0] > lo[0]) {
    out[mid[1]] = ((mid[0] - lo[0]) * s) / (hi[0] - lo[0]);
    out[hi[1]] = s;
  } else {
    out[mid[1]] = 0;
    out[hi[1]] = 0;
  }
  out[lo[1]] = 0;
  return out;
}

/**
 * Composite one stamp pixel with blend mode.
 * srcR/G/B is the brush color (0..1), srcA is the effective stamp alpha (0..1).
 * dstR/G/B/A is the existing pixel (0..1).
 * Returns [outR, outG, outB, outA] normalized 0..1.
 *
 * "behind": paint into transparent areas only (dst-over with dst alpha gate).
 * "clear": erase — output alpha = dstA * (1 - srcA).
 * All other modes: use Adobe-spec blended color with Porter-Duff source-over alpha.
 */
export function applyBlendMode(
  mode: string,
  srcR: number, srcG: number, srcB: number, srcA: number,
  dstR: number, dstG: number, dstB: number, dstA: number,
): [number, number, number, number] {
  const clamp = (x: number) => x < 0 ? 0 : x > 1 ? 1 : x;

  // ── Painting-only modes ──────────────────────────────────────────────
  if (mode === "clear") {
    // Erase: reduce dst alpha by src alpha.
    return [dstR, dstG, dstB, clamp(dstA * (1 - srcA))];
  }

  if (mode === "behind") {
    // Paint only into transparent areas.
    // Effective src alpha is gated by (1 - dstA): fully opaque dst is unchanged.
    const effA = srcA * (1 - dstA);
    if (effA <= 0) return [dstR, dstG, dstB, dstA];
    const outA = effA + dstA * (1 - effA);
    if (outA <= 0) return [0, 0, 0, 0];
    const outR = (srcR * effA + dstR * dstA * (1 - effA)) / outA;
    const outG = (srcG * effA + dstG * dstA * (1 - effA)) / outA;
    const outB = (srcB * effA + dstB * dstA * (1 - effA)) / outA;
    return [clamp(outR), clamp(outG), clamp(outB), clamp(outA)];
  }

  // ── Normal (fast path, same as the original branch) ─────────────────
  if (mode === "normal" || mode === "" || !mode) {
    const outA = srcA + dstA * (1 - srcA);
    if (outA <= 0) return [0, 0, 0, 0];
    return [
      clamp((srcR * srcA + dstR * dstA * (1 - srcA)) / outA),
      clamp((srcG * srcA + dstG * dstA * (1 - srcA)) / outA),
      clamp((srcB * srcA + dstB * dstA * (1 - srcA)) / outA),
      clamp(outA),
    ];
  }

  // ── Dissolve ─────────────────────────────────────────────────────────
  if (mode === "dissolve") {
    // Each pixel is randomly either fully src or fully dst, proportional to srcA.
    if (Math.random() < srcA) {
      const outA = dstA < srcA ? srcA : dstA;
      return [srcR, srcG, srcB, clamp(outA)];
    }
    return [dstR, dstG, dstB, dstA];
  }

  // ── Separable modes ──────────────────────────────────────────────────
  const sepFn = SEP_MODE_MAP[mode];
  let br: number, bg: number, bb: number;

  if (sepFn) {
    br = sepFn(dstR, srcR);
    bg = sepFn(dstG, srcG);
    bb = sepFn(dstB, srcB);
  } else {
    // Non-separable: hue / saturation / color / luminosity / darker-color / lighter-color
    switch (mode) {
      case "hue": {
        const [r, g, b] = setLumRGB(...setSatRGB(srcR, srcG, srcB, satRGB(dstR, dstG, dstB)), lumRGB(dstR, dstG, dstB));
        br = r; bg = g; bb = b; break;
      }
      case "saturation": {
        const [r, g, b] = setLumRGB(...setSatRGB(dstR, dstG, dstB, satRGB(srcR, srcG, srcB)), lumRGB(dstR, dstG, dstB));
        br = r; bg = g; bb = b; break;
      }
      case "color": {
        const [r, g, b] = setLumRGB(srcR, srcG, srcB, lumRGB(dstR, dstG, dstB));
        br = r; bg = g; bb = b; break;
      }
      case "luminosity": {
        const [r, g, b] = setLumRGB(dstR, dstG, dstB, lumRGB(srcR, srcG, srcB));
        br = r; bg = g; bb = b; break;
      }
      case "darker-color": {
        if (lumRGB(srcR, srcG, srcB) < lumRGB(dstR, dstG, dstB)) { br = srcR; bg = srcG; bb = srcB; }
        else { br = dstR; bg = dstG; bb = dstB; }
        break;
      }
      case "lighter-color": {
        if (lumRGB(srcR, srcG, srcB) > lumRGB(dstR, dstG, dstB)) { br = srcR; bg = srcG; bb = srcB; }
        else { br = dstR; bg = dstG; bb = dstB; }
        break;
      }
      default:
        // Unknown → fall back to normal
        br = srcR; bg = srcG; bb = srcB;
    }
  }

  // Porter-Duff source-over alpha; Adobe color contribution formula.
  const outA = srcA + dstA * (1 - srcA);
  if (outA <= 0) return [0, 0, 0, 0];

  const cr = (1 - dstA) * srcR + dstA * br;
  const cg = (1 - dstA) * srcG + dstA * bg;
  const cb = (1 - dstA) * srcB + dstA * bb;

  return [
    clamp((srcA * cr + (1 - srcA) * dstA * dstR) / outA),
    clamp((srcA * cg + (1 - srcA) * dstA * dstG) / outA),
    clamp((srcA * cb + (1 - srcA) * dstA * dstB) / outA),
    clamp(outA),
  ];
}

export function stamp(
  buf: PixelBuffer,
  s: BrushStamp,
  /**
   * Optional per-stroke erase-accumulation buffer + ceiling (mirrors the
   * paint-mode non-compounding ceiling in `stampWithCeiling`). When both are
   * provided and `s.mode === "erase"`, cumulative alpha REDUCTION for a
   * pixel within one stroke is clamped so it cannot exceed the ceiling
   * fraction. Omitted by existing callers (undefined) preserves the
   * original unlimited-erase behavior exactly.
   */
  strokeAlphaBuf?: Uint8Array,
  strokeCeiling?: number,
): BBox | null {
  const radiusX = s.roundness && s.roundness < 1
    ? s.radius / Math.sqrt(s.roundness)
    : s.radius;
  const radiusY = s.roundness && s.roundness < 1
    ? s.radius * Math.sqrt(s.roundness)
    : s.radius;
  const cosA = s.angle ? Math.cos(s.angle) : 1;
  const sinA = s.angle ? Math.sin(s.angle) : 0;
  const hasEllipse = s.roundness !== undefined && s.roundness < 1;

  const minX = Math.max(0, Math.floor(s.cx - radiusX));
  const minY = Math.max(0, Math.floor(s.cy - radiusY));
  const maxX = Math.min(buf.width, Math.ceil(s.cx + radiusX));
  const maxY = Math.min(buf.height, Math.ceil(s.cy + radiusY));
  if (minX >= maxX || minY >= maxY) return null;

  const rSq = s.radius * s.radius;
  const hardR = s.radius * s.hardness;
  const featherSpan = s.radius - hardR;
  const data = buf.data;
  const hasSel = s.selMask && s.selMaskW;
  const isWet = s.wetEdges === true;

  for (let py = minY; py < maxY; py++) {
    const dy = py + 0.5 - s.cy;
    for (let px = minX; px < maxX; px++) {
      const dx = px + 0.5 - s.cx;
      // Transform to brush-local space for ellipse/rotation
      let localDx = dx;
      let localDy = dy;
      if (hasEllipse) {
        const rx = dx * cosA + dy * sinA;
        const ry = -dx * sinA + dy * cosA;
        localDx = rx;
        localDy = ry;
      }
      const distSq = (localDx * localDx) / (radiusX * radiusX) + (localDy * localDy) / (radiusY * radiusY);
      if (distSq >= 1) continue;

      // Wet edges: boost alpha near perimeter, reduce in center
      let a: number;
      if (isWet) {
        const edgeDist = Math.sqrt(distSq);
        // Ring profile: alpha peaks at ~0.7 radius, drops toward center
        const ringProfile = edgeDist < 0.3
          ? edgeDist / 0.3
          : 1 - (edgeDist - 0.3) * 0.15;
        a = Math.max(0, Math.min(1, ringProfile)) * s.opacity;
      } else if (featherSpan <= 0) {
        a = s.opacity;
      } else {
        const dist = Math.sqrt(distSq) * s.radius;
        if (dist <= hardR) a = s.opacity;
        else a = ((s.radius - dist) / featherSpan) * s.opacity;
      }
      if (a <= 0) continue;

      // Selection masking: skip where mask === 0, attenuate where partial.
      if (hasSel) {
        const selIdx = py * s.selMaskW! + px;
        const selVal = s.selMask![selIdx] ?? 0;
        if (selVal === 0) continue;
        if (selVal < 255) {
          a *= selVal / 255;
          if (a <= 0) continue;
        }
      }

      // Lock-transparency masking: skip where alpha === 0 (fully transparent).
      if (s.lockAlpha && s.lockAlphaW) {
        const lockIdx = py * s.lockAlphaW + px;
        if ((s.lockAlpha[lockIdx] ?? 0) === 0) continue;
      }

      const pixelIdx = py * buf.width + px;
      const i = pixelIdx * 4;
      if (s.mode === "erase" && s.eraseToColor) {
        // Background layer (#i165): there's no transparency to reveal, so
        // Photoshop's Eraser paints the background color at the stamp's
        // alpha instead of reducing dst alpha — same source-over math as a
        // plain-color "paint" stamp with blendMode "normal".
        const dr = data[i]!;
        const dg = data[i + 1]!;
        const db = data[i + 2]!;
        const da = data[i + 3]! / 255;
        const outA = a + da * (1 - a);
        if (outA > 0) {
          data[i]     = (s.eraseToColor.r * a + dr * da * (1 - a)) / outA;
          data[i + 1] = (s.eraseToColor.g * a + dg * da * (1 - a)) / outA;
          data[i + 2] = (s.eraseToColor.b * a + db * da * (1 - a)) / outA;
          data[i + 3] = outA * 255;
        }
      } else if (s.mode === "erase") {
        const da = data[i + 3]!;
        if (strokeAlphaBuf && strokeCeiling !== undefined) {
          // Non-compounding ceiling for erase: see stampWithCeiling for the
          // derivation (cumulative erasure fraction accumulates the same
          // way source-over alpha buildup does).
          const ceilingA255 = Math.round(strokeCeiling * 255);
          const alreadyErased = strokeAlphaBuf[pixelIdx] ?? 0;
          if (alreadyErased >= ceilingA255) continue; // ceiling already reached
          const headroom = (ceilingA255 - alreadyErased) / 255;
          const effA = Math.min(a, headroom);
          if (effA <= 0) continue;
          data[i + 3] = da * (1 - effA);
          const newAccum = Math.round(alreadyErased + effA * (255 - alreadyErased));
          strokeAlphaBuf[pixelIdx] = Math.min(ceilingA255, newAccum);
        } else {
          data[i + 3] = da * (1 - a);
        }
      } else {
        // "paint" mode: use blendMode for compositing.
        const bm = s.blendMode ?? "normal";
        if (bm === "normal" || bm === "") {
          // Fast path: plain source-over (unchanged from original).
          const dr = data[i]!;
          const dg = data[i + 1]!;
          const db = data[i + 2]!;
          const da = data[i + 3]! / 255;
          const outA = a + da * (1 - a);
          if (outA <= 0) continue;
          data[i]     = (s.r * a + dr * da * (1 - a)) / outA;
          data[i + 1] = (s.g * a + dg * da * (1 - a)) / outA;
          data[i + 2] = (s.b * a + db * da * (1 - a)) / outA;
          data[i + 3] = outA * 255;
        } else {
          const dr = data[i]! / 255;
          const dg = data[i + 1]! / 255;
          const db = data[i + 2]! / 255;
          const da = data[i + 3]! / 255;
          const [outR, outG, outB, outA] = applyBlendMode(
            bm,
            s.r / 255, s.g / 255, s.b / 255, a,
            dr, dg, db, da,
          );
          data[i]     = Math.round(outR * 255);
          data[i + 1] = Math.round(outG * 255);
          data[i + 2] = Math.round(outB * 255);
          data[i + 3] = Math.round(outA * 255);
        }
      }
    }
  }

  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

// Interpolate stamps along a line segment so the stroke is continuous.
// Stride defaults to radius * spacing (Photoshop-style spacing).
export function stampLine(
  buf: PixelBuffer,
  base: Omit<BrushStamp, "cx" | "cy">,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  stride?: number,
): BBox | null {
  const step = stride ?? Math.max(0.5, base.radius * 0.25);
  const dx = toX - fromX;
  const dy = toY - fromY;
  const len = Math.hypot(dx, dy);
  const n = Math.max(1, Math.ceil(len / step));
  let union: BBox | null = null;
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    const bb = stamp(buf, { ...base, cx: fromX + dx * t, cy: fromY + dy * t });
    if (bb) union = union ? mergeBBox(union, bb) : bb;
  }
  return union;
}

/**
 * Like `stampLine` but enforces the per-stroke opacity ceiling.
 * See `stampWithCeiling` for the semantics.
 */
export function stampLineWithCeiling(
  buf: PixelBuffer,
  base: Omit<BrushStamp, "cx" | "cy">,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  strokeAlphaBuf: Uint8Array,
  strokeCeiling: number,
  stride?: number,
): BBox | null {
  const step = stride ?? Math.max(0.5, base.radius * 0.25);
  const dx = toX - fromX;
  const dy = toY - fromY;
  const len = Math.hypot(dx, dy);
  const n = Math.max(1, Math.ceil(len / step));
  let union: BBox | null = null;
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    const bb = stampWithCeiling(buf, { ...base, cx: fromX + dx * t, cy: fromY + dy * t }, strokeAlphaBuf, strokeCeiling);
    if (bb) union = union ? mergeBBox(union, bb) : bb;
  }
  return union;
}

/**
 * Advanced stamp line with full brush dynamics: spacing, scatter,
 * count, size/angle/opacity jitter, color dynamics, wet edges.
 */
export interface AdvancedStampLineParams {
  buf: PixelBuffer;
  base: Omit<BrushStamp, "cx" | "cy" | "r" | "g" | "b">;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  settings: BrushSettings;
  /** Foreground color */
  fgR: number; fgG: number; fgB: number;
  /** Background color (for fg/bg jitter) */
  bgR: number; bgG: number; bgB: number;
  /** Pen pressure (0..1) for pressure-sensitive controls */
  pressure?: number;
  /** Stroke position along the total stroke length (for fade control) */
  strokePos?: number;
  /** Total stroke length (for fade control) */
  strokeLen?: number;
}

export function advancedStampLine(params: AdvancedStampLineParams): BBox | null {
  const { buf, base, fromX, fromY, toX, toY, settings, fgR, fgG, fgB, bgR, bgG, bgB } = params;
  const pressure = params.pressure ?? 1;
  const strokePos = params.strokePos ?? 0;
  const strokeLen = params.strokeLen ?? 1;

  // Compute spacing in doc pixels
  const spacingPx = Math.max(1, base.radius * 2 * settings.spacing);
  const dx = toX - fromX;
  const dy = toY - fromY;
  const len = Math.hypot(dx, dy);
  const strokeDir = len > 0 ? Math.atan2(dy, dx) : 0;

  // Fade control value (0 at start, 1 at end)
  const fadeValue = strokeLen > 0 ? Math.min(1, strokePos / strokeLen) : 0;

  let union: BBox | null = null;
  const numSteps = Math.max(1, Math.ceil(len / spacingPx));

  for (let i = 1; i <= numSteps; i++) {
    const t = i / numSteps;
    const cx = fromX + dx * t;
    const cy = fromY + dy * t;

    // Number of stamps at this spacing interval (count + count jitter)
    const countVariation = settings.countJitter > 0
      ? settings.count * (1 - settings.countJitter + settings.countJitter * Math.random())
      : settings.count;
    const stampsAtInterval = Math.max(1, Math.round(countVariation));

    for (let c = 0; c < stampsAtInterval; c++) {
      // Scatter offset
      let scatterX = 0;
      let scatterY = 0;
      if (settings.scatter > 0) {
        const scatterAmount = settings.scatter * base.radius * 2;
        scatterX = scatterAmount * (Math.random() - 0.5) * 2;
        if (settings.scatterBothAxes) {
          scatterY = scatterAmount * (Math.random() - 0.5) * 2;
        } else {
          // Scatter perpendicular to stroke direction
          scatterY = scatterX;
          scatterX = 0;
        }
      }

      // Size jitter
      const sizeControlValue = getControlValue(
        settings.sizeJitterControl, pressure, fadeValue,
      );
      const sizeMultiplier = applyJitter(1, settings.sizeJitter, settings.sizeJitterControl, sizeControlValue);
      const effectiveRadius = base.radius * sizeMultiplier;

      // Angle jitter
      const angleJitterAmount = settings.angleJitter * Math.PI;
      const effectiveAngle = (base.angle ?? 0) + angleJitterAmount * (Math.random() - 0.5);

      // Roundness jitter
      const roundnessJitterAmount = settings.roundnessJitter;
      const baseRoundness = base.roundness ?? 1;
      const effectiveRoundness = Math.max(
        settings.minRoundness,
        baseRoundness - roundnessJitterAmount * Math.random(),
      );

      // Opacity jitter
      const opacityControlValue = getControlValue(
        settings.opacityJitterControl, pressure, fadeValue,
      );
      const effectiveOpacity = applyJitter(
        base.opacity, settings.opacityJitter, settings.opacityJitterControl, opacityControlValue,
      );

      // Flow jitter
      const flowControlValue = getControlValue(
        settings.flowJitterControl, pressure, fadeValue,
      );
      const effectiveFlow = applyJitter(
        settings.flow, settings.flowJitter, settings.flowJitterControl, flowControlValue,
      );

      // Color dynamics
      let r = fgR, g = fgG, b = fgB;
      if (settings.fgBgJitter > 0) {
        [r, g, b] = mixFgBg(fgR, fgG, fgB, bgR, bgG, bgB, settings.fgBgJitter);
      }
      if (settings.hueJitter > 0) {
        [r, g, b] = applyHueJitter(r, g, b, settings.hueJitter);
      }
      if (settings.purity !== 0) {
        [r, g, b] = applyPurity(r, g, b, settings.purity);
      }

      const stampCx = cx + scatterX;
      const stampCy = cy + scatterY;
      const stampRadius = Math.max(0.5, effectiveRadius);

      const bb = stamp(buf, {
        ...base,
        cx: stampCx,
        cy: stampCy,
        radius: stampRadius,
        opacity: Math.max(0, Math.min(1, effectiveOpacity * effectiveFlow)),
        r, g, b,
        angle: effectiveAngle,
        roundness: effectiveRoundness,
      });
      if (bb) union = union ? mergeBBox(union, bb) : bb;
    }
  }
  return union;
}

/**
 * Get the control value for a jitter control mode.
 * Returns 1 (full jitter) when control is off, or the control input value.
 */
function getControlValue(
  control: string,
  pressure: number,
  fadeValue: number,
): number {
  switch (control) {
    case "pen-pressure":
    case "pen-tilt":
      return pressure;
    case "fade":
      return fadeValue;
    default:
      return 1;
  }
}

/**
 * Airbrush: continuously stamp at a fixed position, building up paint over time.
 * Call repeatedly (e.g., each rAF) while the pointer is held stationary.
 * Returns the bbox of pixels touched, or null.
 */
export interface AirbrushParams {
  buf: PixelBuffer;
  base: Omit<BrushStamp, "cx" | "cy" | "r" | "g" | "b">;
  cx: number;
  cy: number;
  settings: BrushSettings;
  fgR: number; fgG: number; fgB: number;
  bgR: number; bgG: number; bgB: number;
  /** Elapsed time in ms since airbrush started at this position */
  elapsedMs: number;
  /** Delta time in ms since last airbrush call */
  deltaMs: number;
  pressure?: number;
}

export function airbrushStamp(params: AirbrushParams): BBox | null {
  const { buf, base, cx, cy, settings, fgR, fgG, fgB, bgR, bgG, bgB, elapsedMs, deltaMs } = params;
  const pressure = params.pressure ?? 1;

  // Airbrush builds up paint over time: more stamps as time passes
  // Rate controls how fast paint accumulates
  const rate = settings.airbrushRate;
  const stampsThisFrame = Math.max(1, Math.ceil(rate * deltaMs * 0.1));

  let union: BBox | null = null;

  for (let i = 0; i < stampsThisFrame; i++) {
    // Scatter for airbrush spray pattern
    const scatterAmount = base.radius * 1.5;
    const angle = Math.random() * Math.PI * 2;
    const dist = Math.random() * scatterAmount;
    const scatterX = Math.cos(angle) * dist;
    const scatterY = Math.sin(angle) * dist;

    // Size jitter
    const sizeMultiplier = applyJitter(1, settings.sizeJitter, settings.sizeJitterControl, pressure);
    const effectiveRadius = base.radius * sizeMultiplier;

    // Opacity decreases over time for natural spray effect
    const timeFactor = Math.max(0.1, 1 - elapsedMs * 0.0003);
    const effectiveOpacity = base.opacity * timeFactor * settings.flow;

    // Color dynamics
    let r = fgR, g = fgG, b = fgB;
    if (settings.fgBgJitter > 0) {
      [r, g, b] = mixFgBg(fgR, fgG, fgB, bgR, bgG, bgB, settings.fgBgJitter);
    }

    const bb = stamp(buf, {
      ...base,
      cx: cx + scatterX,
      cy: cy + scatterY,
      radius: Math.max(0.5, effectiveRadius),
      opacity: Math.max(0, Math.min(1, effectiveOpacity)),
      r, g, b,
    });
    if (bb) union = union ? mergeBBox(union, bb) : bb;
  }

  return union;
}

export function mergeBBox(a: BBox, b: BBox): BBox {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const x2 = Math.max(a.x + a.w, b.x + b.w);
  const y2 = Math.max(a.y + a.h, b.y + b.h);
  return { x, y, w: x2 - x, h: y2 - y };
}

/**
 * Stamp a brush onto `buf` with a per-stroke opacity ceiling.
 *
 * Photoshop's single-stroke non-compounding semantics: within one continuous
 * stroke the Opacity setting is a hard ceiling — overlapping stamps cannot
 * push any pixel's alpha above `strokeCeiling`. Only lifting and re-pressing
 * the mouse allows further buildup.
 *
 * @param buf           Target pixel buffer (the layer).
 * @param s             Stamp parameters (opacity here is the Flow, 0..1).
 * @param strokeAlphaBuf Per-pixel alpha already applied this stroke (0..255,
 *                      same dimensions as buf). Mutated in-place.
 * @param strokeCeiling The stroke Opacity ceiling (0..1).
 */
export function stampWithCeiling(
  buf: PixelBuffer,
  s: BrushStamp,
  strokeAlphaBuf: Uint8Array,
  strokeCeiling: number,
): BBox | null {
  const radiusX = s.roundness && s.roundness < 1
    ? s.radius / Math.sqrt(s.roundness)
    : s.radius;
  const radiusY = s.roundness && s.roundness < 1
    ? s.radius * Math.sqrt(s.roundness)
    : s.radius;
  const cosA = s.angle ? Math.cos(s.angle) : 1;
  const sinA = s.angle ? Math.sin(s.angle) : 0;
  const hasEllipse = s.roundness !== undefined && s.roundness < 1;

  const minX = Math.max(0, Math.floor(s.cx - radiusX));
  const minY = Math.max(0, Math.floor(s.cy - radiusY));
  const maxX = Math.min(buf.width, Math.ceil(s.cx + radiusX));
  const maxY = Math.min(buf.height, Math.ceil(s.cy + radiusY));
  if (minX >= maxX || minY >= maxY) return null;

  const rSq = s.radius * s.radius;
  const hardR = s.radius * s.hardness;
  const featherSpan = s.radius - hardR;
  const data = buf.data;
  const hasSel = s.selMask && s.selMaskW;
  const isWet = s.wetEdges === true;
  const ceilingA255 = Math.round(strokeCeiling * 255);

  for (let py = minY; py < maxY; py++) {
    const dy = py + 0.5 - s.cy;
    for (let px = minX; px < maxX; px++) {
      const dx = px + 0.5 - s.cx;
      let localDx = dx;
      let localDy = dy;
      if (hasEllipse) {
        const rx = dx * cosA + dy * sinA;
        const ry = -dx * sinA + dy * cosA;
        localDx = rx;
        localDy = ry;
      }
      const distSq = (localDx * localDx) / (radiusX * radiusX) + (localDy * localDy) / (radiusY * radiusY);
      if (distSq >= 1) continue;

      // Wet edges: boost alpha near perimeter
      let a: number;
      if (isWet) {
        const edgeDist = Math.sqrt(distSq);
        const ringProfile = edgeDist < 0.3
          ? edgeDist / 0.3
          : 1 - (edgeDist - 0.3) * 0.15;
        a = Math.max(0, Math.min(1, ringProfile)) * s.opacity;
      } else if (featherSpan <= 0) {
        a = s.opacity;
      } else {
        const dist = Math.sqrt(distSq) * s.radius;
        if (dist <= hardR) a = s.opacity;
        else a = ((s.radius - dist) / featherSpan) * s.opacity;
      }
      if (a <= 0) continue;

      // Selection masking
      if (hasSel) {
        const selIdx = py * s.selMaskW! + px;
        const selVal = s.selMask![selIdx] ?? 0;
        if (selVal === 0) continue;
        if (selVal < 255) {
          a *= selVal / 255;
          if (a <= 0) continue;
        }
      }

      // Lock-transparency masking
      if (s.lockAlpha && s.lockAlphaW) {
        const lockIdx = py * s.lockAlphaW + px;
        if ((s.lockAlpha[lockIdx] ?? 0) === 0) continue;
      }

      const pixelIdx = py * buf.width + px;
      const i = pixelIdx * 4;

      if (s.mode === "erase" && s.eraseToColor) {
        // Background layer (#i165): paint the background color, ceiling-
        // limited the same way the paint-mode branch below limits color
        // buildup, instead of reducing dst alpha.
        const alreadyApplied = strokeAlphaBuf[pixelIdx] ?? 0;
        if (alreadyApplied >= ceilingA255) continue; // ceiling already reached

        const headroom = (ceilingA255 - alreadyApplied) / 255;
        const effA = Math.min(a, headroom);
        if (effA <= 0) continue;

        const dr = data[i]!;
        const dg = data[i + 1]!;
        const db = data[i + 2]!;
        const da = data[i + 3]! / 255;
        const outA = effA + da * (1 - effA);
        if (outA > 0) {
          data[i]     = (s.eraseToColor.r * effA + dr * da * (1 - effA)) / outA;
          data[i + 1] = (s.eraseToColor.g * effA + dg * da * (1 - effA)) / outA;
          data[i + 2] = (s.eraseToColor.b * effA + db * da * (1 - effA)) / outA;
          data[i + 3] = outA * 255;
        }

        const newAccum = Math.round(alreadyApplied + effA * (255 - alreadyApplied));
        strokeAlphaBuf[pixelIdx] = Math.min(ceilingA255, newAccum);
      } else if (s.mode === "erase") {
        // Non-compounding ceiling: mirrors the paint-mode clamp below, but
        // for erasure. Repeated erasure composes as 1 - product(1 - a_i),
        // which accumulates via the exact same source-over-style formula
        // paint uses to build up alpha — so we can reuse strokeAlphaBuf to
        // track cumulative erased fraction (0..ceilingA255) and clamp each
        // dab's effective alpha so the cumulative REDUCTION for this pixel
        // this stroke cannot exceed the Opacity-derived ceiling.
        const alreadyErased = strokeAlphaBuf[pixelIdx] ?? 0;
        if (alreadyErased >= ceilingA255) continue; // ceiling already reached

        const headroom = (ceilingA255 - alreadyErased) / 255;
        const effA = Math.min(a, headroom);
        if (effA <= 0) continue;

        const da = data[i + 3]!;
        data[i + 3] = da * (1 - effA);

        const newAccum = Math.round(alreadyErased + effA * (255 - alreadyErased));
        strokeAlphaBuf[pixelIdx] = Math.min(ceilingA255, newAccum);
      } else {
        // Non-compounding ceiling: the cumulative alpha for this pixel this
        // stroke cannot exceed ceilingA255.
        const alreadyApplied = strokeAlphaBuf[pixelIdx] ?? 0;
        if (alreadyApplied >= ceilingA255) continue; // ceiling already reached

        // How much headroom is left before the ceiling?
        const headroom = (ceilingA255 - alreadyApplied) / 255;

        // Effective flow alpha clamped to headroom
        const effA = Math.min(a, headroom);
        if (effA <= 0) continue;

        const bm = s.blendMode ?? "normal";
        if (bm === "normal" || bm === "") {
          const dr = data[i]!;
          const dg = data[i + 1]!;
          const db = data[i + 2]!;
          const da = data[i + 3]! / 255;
          const outA = effA + da * (1 - effA);
          if (outA <= 0) continue;
          data[i]     = (s.r * effA + dr * da * (1 - effA)) / outA;
          data[i + 1] = (s.g * effA + dg * da * (1 - effA)) / outA;
          data[i + 2] = (s.b * effA + db * da * (1 - effA)) / outA;
          data[i + 3] = outA * 255;
        } else {
          const dr = data[i]! / 255;
          const dg = data[i + 1]! / 255;
          const db = data[i + 2]! / 255;
          const da = data[i + 3]! / 255;
          const [outR, outG, outB, outA] = applyBlendMode(
            bm,
            s.r / 255, s.g / 255, s.b / 255, effA,
            dr, dg, db, da,
          );
          data[i]     = Math.round(outR * 255);
          data[i + 1] = Math.round(outG * 255);
          data[i + 2] = Math.round(outB * 255);
          data[i + 3] = Math.round(outA * 255);
        }

        // Update the per-pixel accumulated alpha tracking. We track the
        // maximum alpha that source-over with effA would produce from zero:
        //   new_max = old_max + effA * (1 - old_max/255)
        // This matches the source-over alpha accumulation formula.
        const newAccum = Math.round(alreadyApplied + effA * (255 - alreadyApplied));
        strokeAlphaBuf[pixelIdx] = Math.min(ceilingA255, newAccum);
      }
    }
  }

  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}
